import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dumbbell, Trash2, Edit2, Check, X } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

interface TrainingPlanCardProps {
  records: any[];
  onRecordsChange: (updatedRecords: any[]) => void;
}

export function TrainingPlanCard({ records, onRecordsChange }: TrainingPlanCardProps) {
  const trainingRecords = records.filter(r => r.type === 'training_plan');
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editingExercise, setEditingExercise] = useState<{
    recordIdx: number;
    groupIdx: number;
    exerciseIdx: number;
  } | null>(null);
  const [editForm, setEditForm] = useState<{
    exercise: string;
    sets: number | null;
    reps: string;
    load: number | null;
    load_unit: string;
  }>({
    exercise: '',
    sets: null,
    reps: '',
    load: null,
    load_unit: 'kg'
  });

  if (trainingRecords.length === 0) return null;

  const handleDelete = (idx: number) => {
    const originalIndex = records.indexOf(trainingRecords[idx]);
    setDeleteIndex(originalIndex);
  };

  const confirmDelete = () => {
    if (deleteIndex === null) return;
    const updated = records.filter((_, i) => i !== deleteIndex);
    onRecordsChange(updated);
    setDeleteIndex(null);
    toast.success("Plano de treino excluído!");
  };

  const getTotalExercises = (record: any) => {
    if (!record.training_groups || !Array.isArray(record.training_groups)) return 0;
    return record.training_groups.reduce((total: number, group: any) =>
      total + (group.exercises?.length || 0), 0
    );
  };

  const startEditing = (recordIdx: number, groupIdx: number, exerciseIdx: number) => {
    const record = trainingRecords[recordIdx];
    const group = record.training_groups[groupIdx];
    const exercise = group.exercises[exerciseIdx];

    setEditingExercise({ recordIdx, groupIdx, exerciseIdx });
    setEditForm({
      exercise: exercise.exercise || '',
      sets: exercise.sets || null,
      reps: exercise.reps || '',
      load: exercise.load || null,
      load_unit: exercise.load_unit || 'kg'
    });
  };

  const cancelEditing = () => {
    setEditingExercise(null);
    setEditForm({
      exercise: '',
      sets: null,
      reps: '',
      load: null,
      load_unit: 'kg'
    });
  };

  const saveExercise = () => {
    if (!editingExercise) return;

    const { recordIdx, groupIdx, exerciseIdx } = editingExercise;
    const updatedRecords = [...records];
    const recordIndex = records.indexOf(trainingRecords[recordIdx]);
    const updatedRecord = { ...updatedRecords[recordIndex] };
    const updatedGroups = [...updatedRecord.training_groups];
    const updatedGroup = { ...updatedGroups[groupIdx] };
    const updatedExercises = [...updatedGroup.exercises];

    updatedExercises[exerciseIdx] = {
      ...updatedExercises[exerciseIdx],
      exercise: editForm.exercise,
      sets: editForm.sets,
      reps: editForm.reps,
      load: editForm.load,
      load_unit: editForm.load_unit
    };

    updatedGroup.exercises = updatedExercises;
    updatedGroups[groupIdx] = updatedGroup;
    updatedRecord.training_groups = updatedGroups;
    updatedRecords[recordIndex] = updatedRecord;

    onRecordsChange(updatedRecords);
    setEditingExercise(null);
    toast.success("Exercício atualizado!");
  };

  const deleteExercise = (recordIdx: number, groupIdx: number, exerciseIdx: number) => {
    const updatedRecords = [...records];
    const recordIndex = records.indexOf(trainingRecords[recordIdx]);
    const updatedRecord = { ...updatedRecords[recordIndex] };
    const updatedGroups = [...updatedRecord.training_groups];
    const updatedGroup = { ...updatedGroups[groupIdx] };
    const updatedExercises = updatedGroup.exercises.filter((_: any, idx: number) => idx !== exerciseIdx);

    updatedGroup.exercises = updatedExercises;
    updatedGroups[groupIdx] = updatedGroup;
    updatedRecord.training_groups = updatedGroups;
    updatedRecords[recordIndex] = updatedRecord;

    onRecordsChange(updatedRecords);
    toast.success("Exercício excluído!");
  };

  return (
    <div className="space-y-4">
      {trainingRecords.map((record, idx) => {
        const totalExercises = getTotalExercises(record);
        const groupCount = record.training_groups?.length || 0;
        const isMainRecord = record.is_main_record === true;

        return (
          <Card key={idx} className="border-orange-600/20">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Dumbbell className="h-5 w-5 text-orange-600" />
                  <CardTitle className="text-lg text-orange-600">Plano de Treino</CardTitle>
                  {!isMainRecord && (
                    <Badge variant="secondary">
                      {groupCount} {groupCount === 1 ? 'grupo' : 'grupos'} · {totalExercises} {totalExercises === 1 ? 'exercício' : 'exercícios'}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(idx)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {record.plan_name && (
                <p className="text-sm text-muted-foreground mt-1">{record.plan_name}</p>
              )}
            </CardHeader>

            {!isMainRecord && (
              <CardContent className="space-y-4">
                {record.training_groups && record.training_groups.length > 0 ? (
                  <Accordion type="multiple" className="w-full">
                    {record.training_groups.map((group: any, groupIdx: number) => (
                      <AccordionItem key={groupIdx} value={`group-${groupIdx}`}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{group.group_name}</span>
                            <Badge variant="outline" className="ml-2">
                              {group.exercises?.length || 0} {group.exercises?.length === 1 ? 'exercício' : 'exercícios'}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {group.exercises && group.exercises.length > 0 ? (
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Exercício</TableHead>
                                    <TableHead className="text-center">Séries</TableHead>
                                    <TableHead className="text-center">Reps</TableHead>
                                    <TableHead className="text-center">Carga</TableHead>
                                    <TableHead className="w-24"></TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {group.exercises.map((exercise: any, exerciseIdx: number) => {
                                    const isEditing = editingExercise?.recordIdx === idx &&
                                      editingExercise?.groupIdx === groupIdx &&
                                      editingExercise?.exerciseIdx === exerciseIdx;

                                    return (
                                      <TableRow key={exerciseIdx} className="hover:bg-muted/50">
                                        <TableCell className="font-medium">
                                          {isEditing ? (
                                            <Input
                                              value={editForm.exercise}
                                              onChange={(e) => setEditForm({ ...editForm, exercise: e.target.value })}
                                              className="h-8"
                                              placeholder="Nome do exercício"
                                            />
                                          ) : (
                                            exercise.exercise
                                          )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {isEditing ? (
                                            <Input
                                              type="number"
                                              value={editForm.sets || ''}
                                              onChange={(e) => setEditForm({ ...editForm, sets: e.target.value ? parseInt(e.target.value) : null })}
                                              className="h-8 w-16 text-center"
                                              placeholder="0"
                                            />
                                          ) : (
                                            <span className="font-bold text-secondary">
                                              {exercise.sets ? `${exercise.sets}x` : '-'}
                                            </span>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {isEditing ? (
                                            <Input
                                              value={editForm.reps}
                                              onChange={(e) => setEditForm({ ...editForm, reps: e.target.value })}
                                              className="h-8 w-20 text-center"
                                              placeholder="12"
                                            />
                                          ) : (
                                            <span className="font-bold text-secondary">
                                              {exercise.reps || '-'}
                                            </span>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {isEditing ? (
                                            <div className="flex items-center gap-1 justify-center">
                                              <Input
                                                type="number"
                                                value={editForm.load || ''}
                                                onChange={(e) => setEditForm({ ...editForm, load: e.target.value ? parseFloat(e.target.value) : null })}
                                                className="h-8 w-16 text-center"
                                                placeholder="0"
                                              />
                                              <Input
                                                value={editForm.load_unit}
                                                onChange={(e) => setEditForm({ ...editForm, load_unit: e.target.value })}
                                                className="h-8 w-12 text-center"
                                                placeholder="kg"
                                              />
                                            </div>
                                          ) : (
                                            exercise.load ? (
                                              <span className="font-medium">
                                                {exercise.load}{exercise.load_unit || 'kg'}
                                              </span>
                                            ) : '-'
                                          )}
                                        </TableCell>
                                        <TableCell className="w-24">
                                          {isEditing ? (
                                            <div className="flex items-center gap-1 justify-end">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={saveExercise}
                                                className="h-7 w-7 p-0"
                                              >
                                                <Check className="h-4 w-4 text-green-600" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={cancelEditing}
                                                className="h-7 w-7 p-0"
                                              >
                                                <X className="h-4 w-4 text-red-600" />
                                              </Button>
                                            </div>
                                          ) : (
                                            <div className="flex items-center gap-1 justify-end">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => startEditing(idx, groupIdx, exerciseIdx)}
                                                className="h-7 w-7 p-0"
                                              >
                                                <Edit2 className="h-3.5 w-3.5" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => deleteExercise(idx, groupIdx, exerciseIdx)}
                                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                              >
                                                <Trash2 className="h-3.5 w-3.5" />
                                              </Button>
                                            </div>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic py-4">
                              Nenhum exercício neste grupo
                            </p>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : null}

                {record.notes && (
                  <div className="bg-muted p-3 rounded-md mt-4">
                    <p className="text-sm text-muted-foreground">
                      <strong>Orientações:</strong> {record.notes}
                    </p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}

      <AlertDialog open={deleteIndex !== null} onOpenChange={() => setDeleteIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este plano de treino? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}